import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Megaphone, Copy, Sparkles, Trophy, Calendar, TrendingUp, Dumbbell,
  UtensilsCrossed, ChevronDown, ChevronUp, Check, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATES = [
  { id: 'challenge_results', icon: Trophy, label: 'Resultats Challenge', desc: 'Classement, scores, progression des participants', color: '#FFB800' },
  { id: 'event_announce', icon: Calendar, label: 'Annonce Evenement', desc: 'Promouvoir un prochain evenement special', color: '#FF6B35' },
  { id: 'motivation', icon: TrendingUp, label: 'Post Motivation', desc: 'Citation, stats inspirantes de la meute', color: '#2e7d32' },
  { id: 'training_recap', icon: Dumbbell, label: 'Recap Entrainement', desc: 'Resume de la semaine, exercices, groupes', color: '#FF00B8' },
  { id: 'nutrition_tip', icon: UtensilsCrossed, label: 'Conseil Nutrition', desc: 'Bon plan, recette, conseil nutritionnel', color: '#FF6B35' },
  { id: 'custom', icon: Sparkles, label: 'Libre', desc: 'Decris exactement ce que tu veux', color: '#A1A1AA' },
];

export default function CommunicationTab() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customRequest, setCustomRequest] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [appData, setAppData] = useState(null);
  const [showData, setShowData] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    apiClient.get('/admin/communication-data').then(r => setAppData(r.data)).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!selectedTemplate && !customRequest.trim()) {
      toast.error('Choisis un type de visuel ou decris ta demande');
      return;
    }
    setGenerating(true);
    setResult(null);
    setCopied(false);
    try {
      const { data } = await apiClient.post('/admin/generate-visual-prompt', {
        template: selectedTemplate,
        custom_request: customRequest.trim(),
      });
      setResult(data);
      setHistory(prev => [data, ...prev].slice(0, 10));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Prompt copie !');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleReset = () => {
    setSelectedTemplate(null);
    setCustomRequest('');
    setResult(null);
    setCopied(false);
  };

  return (
    <div className="p-4 space-y-4" data-testid="communication-tab">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF00B8] to-[#FFB800] flex items-center justify-center">
          <Megaphone className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Generateur de Prompts
          </h2>
          <p className="text-[11px] text-zinc-500">Cree des visuels reseaux sociaux avec les donnees La Meute</p>
        </div>
      </div>

      {/* Template selector */}
      <div>
        <Label className="text-zinc-400 text-xs mb-2 block">Type de visuel</Label>
        <div className="grid grid-cols-2 gap-2">
          {TEMPLATES.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedTemplate(t.id === selectedTemplate ? null : t.id)}
              data-testid={`template-${t.id}`}
              className={`p-3 rounded-lg text-left transition-all ${
                selectedTemplate === t.id
                  ? 'bg-[#FF6B35]/15 border-2 border-[#FF6B35]/50'
                  : 'bg-[#1B1B1B] border border-[#2A2A2E] hover:border-zinc-500'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <t.icon className="w-4 h-4" style={{ color: t.color }} />
                <span className={`text-xs font-semibold ${selectedTemplate === t.id ? 'text-[#FF6B35]' : 'text-white'}`}>{t.label}</span>
              </div>
              <p className="text-[10px] text-zinc-500 leading-tight">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* App data preview */}
      {appData && (
        <button
          onClick={() => setShowData(!showData)}
          className="w-full flex items-center justify-between p-3 bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg text-left"
          data-testid="toggle-app-data"
        >
          <span className="text-xs text-zinc-400">Donnees disponibles de l'app</span>
          {showData ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
      )}
      {showData && appData && (
        <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-lg p-3 space-y-2 text-[10px] text-zinc-400 font-mono max-h-48 overflow-y-auto">
          <p>Membres actifs: {appData.total_members}</p>
          <p>Challenge actif: {appData.challenge_actif ? 'Oui' : 'Non'}</p>
          {appData.challenge_actif && (
            <>
              <p>Participants challenge: {appData.challenge_participants}</p>
              <p>Top 3 recomp: {appData.top3_recomp?.map(t => `${t.nom} (${t.score})`).join(', ') || 'Aucun'}</p>
              <p>Top 3 gras: {appData.top3_gras?.map(t => `${t.nom} (${t.score})`).join(', ') || 'Aucun'}</p>
              <p>Top 3 muscle: {appData.top3_muscle?.map(t => `${t.nom} (${t.score})`).join(', ') || 'Aucun'}</p>
            </>
          )}
          <p>Evenements: {appData.events_count}</p>
          <p>Defis actifs: {appData.challenges_count}</p>
        </div>
      )}

      {/* Custom request */}
      <div>
        <Label className="text-zinc-400 text-xs mb-1 block">
          {selectedTemplate === 'custom' ? 'Decris ton visuel *' : 'Instructions supplementaires (optionnel)'}
        </Label>
        <Textarea
          value={customRequest}
          onChange={e => setCustomRequest(e.target.value)}
          placeholder={selectedTemplate === 'custom'
            ? "Ex: Je veux un visuel pour annoncer le debut du challenge avec les noms des 5 participants..."
            : "Ex: Met en avant Moetu qui a le meilleur score, ajoute une citation motivante..."}
          className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[80px] text-sm"
          data-testid="custom-request-input"
        />
      </div>

      {/* Generate button */}
      <Button
        onClick={handleGenerate}
        disabled={generating || (!selectedTemplate && !customRequest.trim())}
        className="w-full bg-gradient-to-r from-[#FF00B8] to-[#FFB800] text-white font-bold hover:opacity-90 disabled:opacity-40 h-12"
        data-testid="generate-prompt-btn"
      >
        {generating ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Generation en cours...
          </div>
        ) : (
          <><Sparkles className="w-4 h-4 mr-2" /> Generer le prompt</>
        )}
      </Button>

      {/* Result */}
      {result && (
        <div className="space-y-3 animate-fade-in" data-testid="prompt-result">
          {/* Main prompt */}
          <div className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-[#FF6B35]/10 border-b border-[#FF6B35]/20">
              <span className="text-xs font-semibold text-[#FF6B35]">Prompt Image Generation</span>
              <button
                onClick={() => handleCopy(result.prompt_image)}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                  copied ? 'bg-green-500/20 text-green-400' : 'bg-[#FF6B35]/20 text-[#FF6B35] hover:bg-[#FF6B35]/30'
                }`}
                data-testid="copy-image-prompt"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copie !' : 'Copier'}
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{result.prompt_image}</p>
            </div>
          </div>

          {/* Caption text */}
          {result.caption && (
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 bg-[#1B1B1B] border-b border-[#2A2A2E]">
                <span className="text-xs font-semibold text-[#FFB800]">Texte / Caption</span>
                <button
                  onClick={() => handleCopy(result.caption)}
                  className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-[#FFB800]/15 text-[#FFB800] hover:bg-[#FFB800]/25"
                  data-testid="copy-caption"
                >
                  <Copy className="w-3 h-3" /> Copier
                </button>
              </div>
              <div className="p-4">
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{result.caption}</p>
              </div>
            </div>
          )}

          {/* Tips */}
          {result.tips && (
            <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-lg p-3">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 font-semibold">Conseils</p>
              <p className="text-xs text-zinc-400">{result.tips}</p>
            </div>
          )}

          <Button onClick={handleReset} variant="outline" className="w-full border-[#2A2A2E] text-zinc-400 hover:text-white" data-testid="reset-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Nouveau prompt
          </Button>
        </div>
      )}

      {/* History */}
      {history.length > 1 && !result && (
        <div className="space-y-2">
          <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Historique recent</p>
          {history.slice(1).map((h, i) => (
            <button
              key={i}
              onClick={() => { setResult(h); setCopied(false); }}
              className="w-full bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-3 text-left hover:border-zinc-500 transition-colors"
            >
              <p className="text-xs text-white truncate">{h.prompt_image?.slice(0, 80)}...</p>
              <p className="text-[10px] text-zinc-500 mt-1">{h.template_used || 'Libre'}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
